import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Input,
  Field,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular, DismissCircleRegular, Wand20Regular } from '@fluentui/react-icons';
import { useDataModels } from '../contexts/DataModelsContext';
import { useDashboard } from '../contexts/DashboardContext';
import { useTopBarTitle, useTopBarBreadcrumbs, useTopBarActions } from '../contexts/TopBarContext';
import { dataModelDiscovery } from '../utils/dataModelDiscovery';
import KeyQuestionCard from '../components/discover/KeyQuestionCard';
import KeyInsightsSection from '../components/discover/KeyInsightsSection';
import BusinessDatasetsSection from '../components/discover/BusinessDatasetsSection';
import BuildingModelView from '../components/discover/BuildingModelView';
import { useWorkspaceAccess } from '../components/security/WorkspaceAccess';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    ...shorthands.overflow('hidden'),
  },
  stickyBar: {
    flexShrink: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ...shorthands.padding('8px', '40px'),
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    justifyContent: 'flex-start',
    minWidth: 0,
  },
  searchField: {
    flex: '1 1 auto',
    maxWidth: '600px',
    minWidth: '240px',
  },
  scrollArea: {
    flex: '1 1 auto',
    overflowY: 'auto',
    ...shorthands.padding('16px', '40px'),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pageTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  sparkle: {
    fontSize: '18px',
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  metaRow: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    whiteSpace: 'nowrap',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    marginBottom: '32px',
  },
  sectionHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    marginBottom: '12px',
  },
  sectionSubtitle: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  questionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '18px',
    '@media (max-width: 1200px)': {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  insightsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
  },
  dashboardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
    gap: '12px',
  },
  advancedLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ...shorthands.padding('12px', '0'),
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('60px', '0'),
    gap: '12px',
  },
});

const DiscoverDataPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentDataModel, loadDataModel, buildJobIds } = useDataModels();
  const { createDashboardWithView } = useDashboard();
  const { runMutation } = useWorkspaceAccess();
  const styles = useStyles();

  const [loadFailed, setLoadFailed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const modelName = currentDataModel?.name ?? 'Data Model';

  useTopBarTitle(modelName);
  useTopBarBreadcrumbs([
    { text: 'Data Models', to: '/data-models' },
    { text: modelName },
  ]);

  const topBarActions = useMemo(() => {
    // Hide Semantic Studio button while data model is building
    if (currentDataModel?.status === 'building' || currentDataModel?.status === 'draft') return null;
    return (
      <Button
        appearance="secondary"
        icon={<Wand20Regular />}
        onClick={() => navigate(`/data-models/${id}/studio`)}
      >
        Semantic Studio
      </Button>
    );
  }, [navigate, id, currentDataModel?.status]);
  useTopBarActions(topBarActions);

  useEffect(() => {
    if (id) {
      setLoadFailed(false);
      loadDataModel(id).catch(() => setLoadFailed(true));
    }
  }, [id, loadDataModel]);

  const discovery = useMemo(() => {
    if (!currentDataModel) return null;
    return dataModelDiscovery(currentDataModel);
  }, [currentDataModel]);

  const filteredQuestions = useMemo(() => {
    if (!discovery) return [];
    if (!searchQuery.trim()) return discovery.keyQuestions;
    const q = searchQuery.toLowerCase();
    return discovery.keyQuestions.filter(
      (kq) =>
        kq.title.toLowerCase().includes(q) ||
        kq.description.toLowerCase().includes(q) ||
        kq.questions.some((qq) => qq.toLowerCase().includes(q)) ||
        kq.relatedMetrics.some((m) => m.toLowerCase().includes(q))
    );
  }, [discovery, searchQuery]);

  const handleCreateDashboard = useCallback((viewId: string, _prompt: string) =>
    runMutation(async () => {
      const newDashboard = await createDashboardWithView(viewId);
      if (newDashboard?.id) {
        navigate(`/dashboards/${newDashboard.id}`);
      }
    }), [createDashboardWithView, navigate, runMutation]);

  const handleOpenDataset = useCallback((viewId: string) => {
    navigate(`/data-models/${id}/views/${viewId}`);
  }, [navigate, id]);


  if (!currentDataModel) {
    if (loadFailed) {
      return (
        <div className={styles.errorContainer}>
          <Text>Data model not found.</Text>
          <Button onClick={() => navigate('/data-models')}>Back to Data Models</Button>
        </div>
      );
    }
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="medium" />
      </div>
    );
  }

  // ─── Building / draft state: AI-first progress experience ───────────────
  if (currentDataModel.status === 'building' || currentDataModel.status === 'draft') {
    return (
      <div className={styles.page}>
        <BuildingModelView
          modelId={id!}
          modelName={modelName}
          fileName={currentDataModel.name}
          jobId={id ? buildJobIds[id] : undefined}
        />
      </div>
    );
  }

  if (!discovery || discovery.keyQuestions.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <div className={styles.titleLeft}>
              <span className={styles.sparkle}>✨</span>
              <Text className={styles.pageTitle}>{modelName}</Text>
            </div>
            <Button appearance="subtle" onClick={() => navigate(`/data-models/${id}/studio`)}>
              Semantic Studio
            </Button>
          </div>
           <Text className={styles.subtitle}>Explore AI-recommended business datasets and dashboards.</Text>
        </div>
        <div className={styles.emptyState}>
          <Text style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }}>
            No analysis workflows could be generated yet.
          </Text>
          <Button appearance="primary" onClick={() => navigate(`/data-models/${id}/studio`)}>
            Open Semantic Studio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Sticky search bar */}
      <div className={styles.stickyBar}>
        <div className={styles.toolbar}>
          <div className={styles.searchField}>
            <Field>
              <Input
                size="medium"
                contentBefore={<SearchRegular />}
                contentAfter={
                  searchQuery ? (
                    <Button
                      appearance="transparent"
                      size="small"
                      icon={<DismissCircleRegular />}
                      onClick={() => setSearchQuery('')}
                      aria-label="Clear search"
                    />
                  ) : undefined
                }
                placeholder="Search dashboards, datasets, metrics..."
                value={searchQuery}
                onChange={(_, data) => setSearchQuery(data.value)}
              />
            </Field>
          </div>
        </div>
        <Text className={styles.metaRow}>
          {discovery.keyQuestions.length} Dashboards • {discovery.businessDatasets.recommended.length + discovery.businessDatasets.secondary.length + discovery.businessDatasets.advanced.length} More Ideas • {discovery.quickInsights.length} Analyses
        </Text>
      </div>

      {/* Scrollable content */}
      <div className={styles.scrollArea}>
      {/* Key Insights */}
      {id && <KeyInsightsSection modelId={id} onCreateDashboard={(viewId) => {
        handleCreateDashboard(viewId, '');
      }} onExplore={(viewId) => {
        handleOpenDataset(viewId);
      }} />}

      {/* Recommended Dashboards */}
      <div className={styles.section}>
        <Text className={styles.sectionTitle}>Dashboard Starters</Text>
        <div className={styles.questionsGrid}>
          {filteredQuestions.slice(0, 6).map((kq) => (
            <KeyQuestionCard
              key={kq.id}
              question={kq}
              onCreateDashboard={handleCreateDashboard}
              onExplore={handleOpenDataset}
            />
          ))}
        </div>
      </div>



      {/* Business Datasets (collapsed) */}
      <BusinessDatasetsSection
        recommended={discovery.businessDatasets.recommended}
        secondary={discovery.businessDatasets.secondary}
        advanced={discovery.businessDatasets.advanced}
        onOpen={handleOpenDataset}
        onCreateDashboard={handleCreateDashboard}
      />
      </div>
    </div>
  );
};

export default DiscoverDataPage;